// Pure derived-metric helpers for the PWHL Game Centre (extracted from
// pages/PWHLGameCentre.jsx). No React/JSX — box-score math only.

function _goalSecs(g) {
    const [mm, ss] = String(g.time || "0:0").split(":").map((n) => parseInt(n, 10) || 0);
    return (g.period || 1) * 100000 + mm * 60 + ss;
}

// Rosterly Insights — derived skater metrics from a synced game's goals/penalties.
export function computeSkaterInsights(data) {
    const skaters = data.skaters || [];
    const goals = data.goals || [];
    const pens = data.penalties || [];
    const ordered = [...goals].filter((g) => g.scorer?.pwhl_id).sort((a, b) => _goalSecs(a) - _goalSecs(b));
    // FG/LG are per GAME (one first + one last goal), NOT per period, and EXCLUDE
    // the shootout (period 5) — a shootout winner is never the game's last goal.
    const realOrdered = ordered.filter((g) => (Number(g.period) || 1) < 5);
    const firstGoalId = realOrdered[0]?.scorer?.pwhl_id || null;
    const lastGoalId = realOrdered.length ? realOrdered[realOrdered.length - 1].scorer.pwhl_id : null;
    const der = {};
    const ensure = (id) => (der[id] = der[id] || { g_ev: 0, g_pp: 0, g_sh: 0, g_en: 0, a1: 0, a2: 0, a_ev: 0, a_pp: 0, a_sh: 0, a_en: 0, gwg: 0, uag: 0, g_p1: 0, g_p2: 0, g_p3: 0, g_ot: 0, g_so: 0, a_p1: 0, a_p2: 0, a_p3: 0, a_ot: 0, sog_p1: 0, sog_p2: 0, sog_p3: 0, fow_p1: 0, fow_p2: 0, fow_p3: 0, pens: [], per: {} });
    // Per-period stat bucket for a player (period 1-3, 4=OT, 5=SO).
    const bp = (d, p) => (d.per[p] = d.per[p] || { g: 0, a: 0, a1: 0, a2: 0, evg: 0, ppg: 0, shg: 0, eng: 0, psg: 0, psa: 0, eva: 0, ppa: 0, sha: 0, ena: 0, gwg: 0, uag: 0, pim: 0, sog: 0, fow: 0, fol: 0, hits: 0, blocks: 0, fg: 0, lg: 0, gwga: 0, fga: 0, lga: 0 });
    goals.forEach((g) => {
        const st = g.strength;
        const sid = g.scorer?.pwhl_id;
        if (sid) {
            const d = ensure(sid);
            if (g.empty_net || st === "EN") d.g_en++;
            else if (st === "PP") d.g_pp++;
            else if (st === "SH") d.g_sh++;
            else if (st === "PS") { d.psg = (d.psg || 0) + 1; d.psa = (d.psa || 0) + 1; }
            else d.g_ev++;
            const per = Number(g.period) || 1;
            if (per === 1) d.g_p1++;
            else if (per === 2) d.g_p2++;
            else if (per === 3) d.g_p3++;
            else if (per === 4) d.g_ot++;
            else d.g_so++;
            if (g.game_winning) d.gwg++;
            if (!g.assists || g.assists.length === 0) d.uag++;
            const pb = bp(d, per > 5 ? 5 : per);
            pb.g++;
            if (g.empty_net || st === "EN") pb.eng++; else if (st === "PP") pb.ppg++; else if (st === "SH") pb.shg++; else if (st === "PS") { pb.psg++; pb.psa++; } else pb.evg++;
            if (g.game_winning) pb.gwg++;
            if (!g.assists || g.assists.length === 0) pb.uag++;
        }
        (g.assists || []).forEach((a, idx) => {
            if (!a?.pwhl_id) return;
            const d = ensure(a.pwhl_id);
            if (idx === 0) d.a1++; else d.a2++;
            if (g.empty_net || st === "EN") d.a_en++;
            else if (st === "PP") d.a_pp++;
            else if (st === "SH") d.a_sh++;
            else d.a_ev++;
            const gp = Number(g.period) || 1;
            if (gp === 1) d.a_p1++; else if (gp === 2) d.a_p2++; else if (gp === 3) d.a_p3++; else if (gp >= 4) d.a_ot++;
            const pb = bp(d, gp > 5 ? 5 : gp);
            pb.a++; if (idx === 0) pb.a1++; else pb.a2++;
            if (g.game_winning) pb.gwga++;
            if (g.empty_net || st === "EN") pb.ena++; else if (st === "PP") pb.ppa++; else if (st === "SH") pb.sha++; else pb.eva++;
        });
    });
    // First / last goal of the GAME (SO excluded) — credited to the period bucket
    // the goal occurred in; the same goal's assisters get FGA / LGA.
    if (realOrdered.length) {
        const fg = realOrdered[0];
        const lg = realOrdered[realOrdered.length - 1];
        const fper = Math.min(4, Number(fg.period) || 1);
        const lper = Math.min(4, Number(lg.period) || 1);
        const fp = fg?.scorer?.pwhl_id;
        const lp = lg?.scorer?.pwhl_id;
        if (fp) bp(ensure(fp), fper).fg++;
        (fg?.assists || []).forEach((a) => { if (a?.pwhl_id) bp(ensure(a.pwhl_id), fper).fga++; });
        if (lp) bp(ensure(lp), lper).lg++;
        (lg?.assists || []).forEach((a) => { if (a?.pwhl_id) bp(ensure(a.pwhl_id), lper).lga++; });
    }
    // MISSED penalty shots (play-by-play) → psa only. Made penalty shots are real
    // goals already counted via the goals feed (which also credits psg/psa).
    (data.pxp || []).forEach((e) => {
        if (e.type !== "penaltyshot") return;
        const pid = e.player_id != null ? String(e.player_id) : null;
        if (!pid) return;
        const detail = String(e.detail || "").toLowerCase();
        const missed = ["saved", "miss", "no goal", "stopped", "blocked", "denied"].some((w) => detail.includes(w));
        if (!missed) return;
        const per = Math.min(4, Number(e.period) || 1);
        const d = ensure(pid);
        d.psa = (d.psa || 0) + 1;
        bp(d, per).psa++;
    });
    pens.forEach((p) => {
        const id = p.player?.pwhl_id;
        if (!id) return;
        const d = ensure(id);
        d.pens.push({ time: p.time, period: p.period, infraction: p.infraction, minutes: p.minutes });
        bp(d, (Number(p.period) || 1) > 5 ? 5 : (Number(p.period) || 1)).pim += (Number(p.minutes) || 0);
    });
    // Per-period shots / faceoff wins from the normalized play-by-play.
    (data.pxp || []).forEach((e) => {
        const pid = e.player_id ? String(e.player_id) : "";
        if (!pid) return;
        const gp = Number(e.period) || 1;
        if (e.type === "shot") {
            const d = ensure(pid);
            if (gp === 1) d.sog_p1++; else if (gp === 2) d.sog_p2++; else if (gp === 3) d.sog_p3++;
            bp(d, gp > 5 ? 5 : gp).sog++;
        } else if (e.type === "faceoff") {
            const d = ensure(pid);
            if (gp === 1) d.fow_p1++; else if (gp === 2) d.fow_p2++; else if (gp === 3) d.fow_p3++;
            bp(d, gp > 5 ? 5 : gp).fow++;
        } else if (e.type === "hit") {
            // Official PBP hit — credited to the hitting player (e.player_id).
            const d = ensure(pid);
            d.hitTot = (d.hitTot || 0) + 1;
            bp(d, gp > 5 ? 5 : gp).hits++;
        } else if (e.type === "blocked_shot") {
            // Official PBP blocked shot — BLOCK is credited to the blocker.
            const bid = e.blocker_id ? String(e.blocker_id) : "";
            if (bid) {
                const d = ensure(bid);
                d.blkTot = (d.blkTot || 0) + 1;
                bp(d, gp > 5 ? 5 : gp).blocks++;
            }
        }
    });
    // Per-shooter shootout tallies (SOA / SOG / SOM) from the synced shootout
    // detail. Falls back to a skater's box `soa`/`sowg` when present.
    const soByPlayer = {};
    (data.shootout || []).forEach((sh) => {
        const id = String(sh.pwhl_id || "");
        if (!id) return;
        const o = (soByPlayer[id] = soByPlayer[id] || { soAtt: 0, soGoal: 0, sowg: 0 });
        o.soAtt++;
        if (sh.is_goal) o.soGoal++;
        if (sh.is_goal && sh.is_gwg) o.sowg++;
    });
    const rows = skaters.map((s) => {
        const id = s.player?.pwhl_id;
        const d = der[id] || {};
        const so = soByPlayer[String(id)] || {};
        const soAtt = so.soAtt != null ? so.soAtt : (s.soa || 0);
        const soGoal = so.soGoal != null ? so.soGoal : (s.so_g || 0);
        const sowg = so.sowg != null ? so.sowg : (s.sowg || 0);
        const ppg = d.g_pp || 0, shg = d.g_sh || 0, eng = d.g_en || 0;
        const ppa = d.a_pp || 0, sha = d.a_sh || 0, ena = d.a_en || 0;
        const fow = s.fow || 0, foa = s.foa || 0;
        return {
            ...s, _id: id,
            hits: d.hitTot != null ? d.hitTot : (s.hits || 0),
            blocks: d.blkTot != null ? d.blkTot : (s.blocks || 0),
            gwg: (d.gwg || 0) || s.gwg || 0,
            a1: d.a1 || 0, a2: d.a2 || 0,
            ppg, ppa, shg, sha, eng, ena,
            evg: d.g_ev || 0, eva: d.a_ev || 0,
            psg: d.psg != null ? d.psg : (s.psg || 0),
            psa: d.psa != null ? d.psa : (s.psa || 0),
            psm: Math.max(0, (d.psa != null ? d.psa : (s.psa || 0)) - (d.psg != null ? d.psg : (s.psg || 0))),
            evp: (d.g_ev || 0) + (d.a_ev || 0),
            ppp: ppg + ppa, shp: shg + sha,
            g_p1: d.g_p1 || 0, g_p2: d.g_p2 || 0, g_p3: d.g_p3 || 0, g_ot: d.g_ot || 0, g_so: d.g_so || 0, uag: d.uag || 0,
            a_p1: d.a_p1 || 0, a_p2: d.a_p2 || 0, a_p3: d.a_p3 || 0, a_ot: d.a_ot || 0,
            sog_p1: d.sog_p1 || 0, sog_p2: d.sog_p2 || 0, sog_p3: d.sog_p3 || 0,
            fow_p1: d.fow_p1 || 0, fow_p2: d.fow_p2 || 0, fow_p3: d.fow_p3 || 0,
            fow, fol: Math.max(0, foa - fow), foPct: foa ? (fow / foa) * 100 : 0,
            soAtt, soGoal, sowg, soMiss: Math.max(0, soAtt - soGoal),
            per: d.per || {},
            pens: d.pens || [],
            firstGoal: id && id === firstGoalId, lastGoal: id && id === lastGoalId,
        };
    });
    const lead = (fn) => rows.reduce((best, r) => (fn(r) > (best ? fn(best) : -1) ? r : best), null);
    const leaders = {
        points: lead((r) => r.p || 0), goals: lead((r) => r.g || 0), assists: lead((r) => r.a || 0),
        fow: lead((r) => r.fow), hits: lead((r) => r.hits || 0), blocks: lead((r) => r.blocks || 0),
        shots: lead((r) => r.shots || r.sog || 0), pim: lead((r) => r.pim || 0),
        gwg: rows.find((r) => (r.gwg || 0) > 0) || null,
        firstGoal: rows.find((r) => r.firstGoal) || null,
        lastGoal: rows.find((r) => r.lastGoal) || null,
    };
    return { rows, leaders };
}

const _mmss = (s) => { s = Math.max(0, Math.round(s || 0)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`; };

// Per-goalie, per-period splits. Combined SA/SV/GA come from the official box
// score (team_stats-accurate); per-period SA/SV/GA are derived from the same
// play-by-play the Period Summary uses, attributed to whichever goalie was in
// net at that moment (goalie-on-ice timeline from goalie_change events).
export function computeGoalieSplits(data) {
    const goalies = data.goalies || [];
    const pxp = data.pxp || [];
    const homeId = data.meta?.home_team_id, awayId = data.meta?.away_team_id;
    // Entering-goalie timeline per team (skip the paired "…for X" leave rows).
    const enters = pxp
        .filter((e) => e.type === "goalie_change" && e.player_id && !/enters the game for/i.test(e.detail || ""))
        .map((e) => ({ team_id: e.team_id, pid: String(e.player_id), period: e.period || 1, secs: e.secs || 0 }))
        .sort((a, b) => (a.period - b.period) || (a.secs - b.secs));
    const timeline = {};
    enters.forEach((e) => (timeline[e.team_id] = timeline[e.team_id] || []).push(e));
    const goalieInNet = (teamId, period, secs) => {
        const tl = timeline[teamId] || [];
        let cur = tl[0]?.pid || null;
        for (const e of tl) {
            if (e.period < period || (e.period === period && e.secs <= secs)) cur = e.pid; else break;
        }
        return cur;
    };
    const splits = {}; // pid -> { periods: { p: { sa, ga } } }
    pxp.forEach((e) => {
        if (e.type !== "shot" && e.type !== "goal") return;
        const defTeam = e.team_id === homeId ? awayId : homeId;
        const pid = goalieInNet(defTeam, e.period, e.secs || 0);
        if (!pid) return;
        const per = Number(e.period) || 1;
        const s = (splits[pid] = splits[pid] || { periods: {} });
        s.periods[per] = s.periods[per] || { sa: 0, ga: 0 };
        s.periods[per].sa++;
        if (e.type === "goal") s.periods[per].ga++;
    });
    // Per-period goalie MINUTES (time in net), from the goalie-change timeline.
    // Regulation periods = 1200s; OT length = last recorded event in P4.
    const periodMax = {};
    pxp.forEach((e) => { const p = Number(e.period) || 1; periodMax[p] = Math.max(periodMax[p] || 0, e.secs || 0); });
    const periodLen = (p) => (p <= 3 ? 1200 : (periodMax[p] || 0));
    const maxPeriod = Math.max(3, ...Object.keys(periodMax).map(Number), 3);
    const secsByGoalie = {};
    const addSec = (pid, p, s) => { if (!pid || s <= 0) return; (secsByGoalie[pid] = secsByGoalie[pid] || {})[p] = ((secsByGoalie[pid] || {})[p] || 0) + s; };
    [homeId, awayId].forEach((teamId) => {
        for (let p = 1; p <= maxPeriod; p++) {
            const L = periodLen(p);
            if (!L) continue;
            const evsP = (timeline[teamId] || []).filter((e) => e.period === p).sort((a, b) => a.secs - b.secs);
            let curPid = goalieInNet(teamId, p, 0), curStart = 0;
            for (const e of evsP) {
                if ((e.secs || 0) <= 0) { curPid = e.pid; continue; }
                addSec(curPid, p, (e.secs || 0) - curStart);
                curPid = e.pid; curStart = e.secs || 0;
            }
            addSec(curPid, p, L - curStart);
        }
    });
    // Per-period goalie G / A (goalies occasionally record points).
    const gaByPlayerPer = {};
    (data.goals || []).forEach((gl) => {
        const p = Number(gl.period) || 1;
        const sid = gl.scorer?.pwhl_id;
        if (sid) { const o = (gaByPlayerPer[String(sid)] = gaByPlayerPer[String(sid)] || {}); (o[p] = o[p] || { g: 0, a: 0 }).g++; }
        (gl.assists || []).forEach((a) => { if (a?.pwhl_id) { const o = (gaByPlayerPer[String(a.pwhl_id)] = gaByPlayerPer[String(a.pwhl_id)] || {}); (o[p] = o[p] || { g: 0, a: 0 }).a++; } });
    });
    // Per-period goalie PIM.
    const pimByPlayerPer = {};
    (data.penalties || []).forEach((pn) => {
        const gid = String(pn.player?.pwhl_id || pn.player?.name || "");
        const p = Number(pn.period) || 1;
        const o = (pimByPlayerPer[gid] = pimByPlayerPer[gid] || {});
        o[p] = (o[p] || 0) + (Number(pn.minutes) || 0);
    });
    // Merge multiple box-score stints for the same goalie into one row.
    const byPid = {}; const order = [];
    goalies.forEach((g) => {
        const pid = String(g.player?.pwhl_id || g.player?.name || "");
        if (!byPid[pid]) { byPid[pid] = { ...g, saves: 0, shots_against: 0, goals_against: 0, toi_seconds: 0, _dec: [] }; order.push(pid); }
        const a = byPid[pid];
        a.saves += g.saves || 0; a.shots_against += g.shots_against || 0; a.goals_against += g.goals_against || 0;
        a.toi_seconds += g.toi_seconds || 0;
        if (g.decision) a._dec.push(g.decision);
    });
    // Per-goalie shootout shots faced / goals allowed (from shootout detail).
    const goalieSo = {};
    (data.shootout || []).forEach((sh) => {
        const gid = String(sh.goalie_pwhl_id || "");
        if (!gid) return;
        const o = (goalieSo[gid] = goalieSo[gid] || { soa: 0, soga: 0 });
        o.soa++;
        if (sh.is_goal) o.soga++;
    });
    // Per-goalie penalty minutes (goalies do take penalties).
    const goaliePim = {};
    (data.penalties || []).forEach((p) => {
        const gid = String(p.player?.pwhl_id || p.player?.name || "");
        if (!gid) return;
        goaliePim[gid] = (goaliePim[gid] || 0) + (Number(p.minutes) || 0);
    });
    return order.map((pid) => {
        const g = byPid[pid];
        const sp = splits[pid] || { periods: {} };
        const gso = goalieSo[String(pid)] || {};
        // Merge per-period SA/GA with derived MIN / PIM / G / A per period.
        const mergedPeriods = {};
        for (let p = 1; p <= 5; p++) {
            const base = (sp.periods || {})[p] || { sa: 0, ga: 0 };
            const ga = (gaByPlayerPer[String(pid)] || {})[p] || { g: 0, a: 0 };
            mergedPeriods[p] = {
                sa: base.sa || 0, ga: base.ga || 0,
                secs: (secsByGoalie[String(pid)] || {})[p] || 0,
                pim: (pimByPlayerPer[String(pid)] || {})[p] || 0,
                g: ga.g || 0, a: ga.a || 0,
            };
        }
        return {
            ...g, _pid: pid,
            decision: g._dec[0] || g.decision || "—",
            toi: g.toi_seconds ? _mmss(g.toi_seconds) : (g.toi || "—"),
            toi_seconds: g.toi_seconds || 0,
            shutout: g.goals_against === 0,
            combined: { sa: g.shots_against, sv: g.saves, ga: g.goals_against },
            periods: mergedPeriods,
            soSa: gso.soa != null ? gso.soa : (g.so_shots_against || 0),
            soGa: gso.soga != null ? gso.soga : (g.so_goals_against || 0),
            psSa: g.ps_shots_against || 0,
            pim: goaliePim[String(pid)] || g.pim || 0,
            goals: g.goals || 0, assists: g.assists || 0, points: g.points || ((g.goals || 0) + (g.assists || 0)),
            psGa: g.ps_goals_against || 0,
        };
    });
}

// Game Notes — notable EVENTS & milestones only (no statistical leaders). Max 5.
export function computeGameNotes(insights, data, m) {
    const rows = insights.rows || [];
    const notes = [];
    const seen = new Set();
    const push = (label, player, sub) => {
        if (!player) return;
        const key = `${label}|${player.pwhl_id || player.name}`;
        if (seen.has(key)) return;
        seen.add(key);
        notes.push({ label, player, _id: player.pwhl_id, sub });
    };
    rows.filter((r) => (r.g || 0) >= 3).sort((a, b) => b.g - a.g)
        .forEach((r) => push(r.g >= 4 ? `${r.g}-Goal Game` : "Hat Trick", r.player, `${r.g} goals`));
    const gw = rows.find((r) => (r.gwg || 0) > 0);
    if (gw) push(m.shootout ? "Shootout Winner" : m.overtime ? "Overtime Winner" : "Game-Winning Goal", gw.player, "");
    (data.goalies || []).filter((g) => g.shutout).forEach((g) => push("Shutout", g.player, `${g.saves} saves`));
    rows.filter((r) => (r.eng || 0) > 0).forEach((r) => push("Empty Net Goal", r.player, ""));
    const fg = rows.find((r) => r.firstGoal);
    if (fg) push("First Goal", fg.player, "");
    const lg = rows.find((r) => r.lastGoal);
    if (lg) push("Last Goal", lg.player, "");
    return notes.slice(0, 5);
}
